
import { useEffect, useMemo, useState } from "react";

import { HttpStatusCode } from "axios";
import {
    Button,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Select,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

import AddIcon from "@/assets/AddIcon";
import { COLORS, DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST } from "@/common/constants";
import ModalDelete from "@/components/Modals/ModalDelete";
import TableEmailTemplates from "@/components/Tables/TableEmailTemplates";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { deleteEmailTemplate, getAllEmailTemplates } from "@/services/emailTemplateService";

import "../styles/ListPage.css";

type CategoryType = "all" | "invoice" | "payment" | "registration" | "nomination" | "sire" | "other";

const CATEGORY_OPTIONS: { label: string; value: CategoryType }[] = [
    { label: "All Categories", value: "all" },
    { label: "Invoice", value: "invoice" },
    { label: "Payment", value: "payment" },
    { label: "Registration", value: "registration" },
    { label: "Nomination", value: "nomination" },
    { label: "SIRE Review", value: "sire" },
    { label: "Other", value: "other" },
];

const resolveCategory = (emailTypeName: string): CategoryType => {
    const value = (emailTypeName || "").toLowerCase();
    if (value.includes("invoice")) return "invoice";
    if (value.includes("payment")) return "payment";
    if (value.includes("register")) return "registration";
    if (value.includes("nomination")) return "nomination";
    if (value.includes("sire")) return "sire";
    return "other";
};

const EmailTemplates = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isOpenPreview,
        onOpen: onOpenPreview,
        onClose: onClosePreview,
    } = useDisclosure();
    const name = useAppSelector((state: RootState) => state.account.name);

    const [currentPageNumber, setCurrentPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [rawDataList, setRawDataList] = useState([]) as any;
    const [hasNextPage, setHasNextPage] = useState(false);
    const [idToDelete, setIdToDelete] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [searchValue, setSearchValue] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [categoryValue, setCategoryValue] = useState<CategoryType>("all");
    const [totalPageNumber, setTotalPageNumber] = useState(1);
    const [previewData, setPreviewData] = useState<any>(null);

    const filteredDataList = useMemo(() => {
        if (categoryValue === "all") {
            return rawDataList;
        }

        return rawDataList.filter((item: any) => resolveCategory(item.emailTypeName) === categoryValue);
    }, [rawDataList, categoryValue]);

    const handleDelete = (id: string) => {
        setIdToDelete(id);
        onOpen();
    };

    const handlePreview = (data: any) => {
        setPreviewData(data);
        onOpenPreview();
    };

    const handleConfirmDelete = async () => {
        try {
            const obj = {
                id: idToDelete,
                modifiedBy: name || "System",
            };
            const response = await deleteEmailTemplate(obj) as any;

            if (response.status === HttpStatusCode.Ok) {
                await getData();
                toast({
                    title: 'Template deleted.',
                    description: "We have deleted the template.",
                    status: 'success',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            } else {
                toast({
                    title: 'Template failed to delete.',
                    description: response?.message || "We failed to delete the template.",
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: 'Delete error',
                description: "An error occurred while deleting the template.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        } finally {
            onClose();
        }
    };

    const handleSearch = () => {
        setSearchValue(searchInput);
        setCurrentPageNumber(DEFAULT_PAGE_NUMBER);
    };

    const getData = async () => {
        setIsLoading(true);

        const param = {
            search: searchValue,
            pageNumber: currentPageNumber,
            pageSize: currentPageSize,
        };

        try {
            const response = await getAllEmailTemplates(param) as any;
            if (response.status === HttpStatusCode.Ok) {
                setRawDataList(response.data || []);
                setTotalPageNumber(response.totalPage || 0);
                setHasNextPage(Boolean(response.hasNextPage));
            } else {
                toast({
                    title: response?.status || 'Error',
                    description: response?.message || 'Failed to fetch email templates',
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to fetch email templates',
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getData();
    }, [currentPageSize, currentPageNumber, searchValue]);

    return (
        <>
            <ModalDelete title="Delete it?" isOpen={isOpen} onClose={onClose} handleConfirmDelete={handleConfirmDelete} />
            <Modal onClose={onClosePreview} isOpen={isOpenPreview} size='6xl' isCentered>
                <ModalOverlay />
                <ModalContent borderRadius={15} padding={2}>
                    <ModalHeader>{previewData?.emailTypeName || 'Email Template Preview'}</ModalHeader>
                    <ModalCloseButton _focus={{ outline: 'none', boxShadow: 'none' }} />
                    <ModalBody>
                        <iframe
                            title='email-template-preview'
                            srcDoc={previewData?.emailTemplateHtmlContent || '<p>No preview available</p>'}
                            style={{ width: '100%', minHeight: 520, border: '1px solid #EAECF0', borderRadius: 8 }}
                        />
                    </ModalBody>
                </ModalContent>
            </Modal>

            <div className="table-container">
                <div className="table-filter-action-container">
                    <div className="table-filter-container">
                        <div className="table-filter-input filter-input-width-5">
                            <Input
                                placeholder='Search'
                                value={searchInput}
                                onChange={(e: any) => setSearchInput(e.target.value)}
                            />
                        </div>
                        <div className="table-filter-input filter-input-width-5">
                            <Select
                                // placeholder='--'
                                style={{ backgroundColor: 'white' }}
                                value={categoryValue}
                                onChange={(e: any) => setCategoryValue(e.target.value)}
                            >
                                {CATEGORY_OPTIONS.map((item) => (
                                    <option key={item.value} value={item.value}>{item.label}</option>
                                ))}
                            </Select>
                        </div>
                        <div>
                            <Button
                                className='table-search-button'
                                bgColor={COLORS.BUTTON.BACKGROUND}
                                color={COLORS.BUTTON.FOREGROUND}
                                variant='solid'
                                borderRadius={10}
                                onClick={handleSearch}
                            >
                                Search
                            </Button>
                        </div>
                        <div className="table-filter-separator" />
                        <Button
                            leftIcon={<AddIcon />}
                            className='table-add-button'
                            bgColor={COLORS.BUTTON.BACKGROUND}
                            color={COLORS.BUTTON.FOREGROUND}
                            variant='solid'
                            borderRadius={10}
                            onClick={() => navigate('/settings/email-templates/add')}
                        >
                            Create Template
                        </Button>
                    </div>
                </div>
                <TableEmailTemplates
                    dataList={filteredDataList}
                    isLoading={isLoading}
                    handleEdit={(id: string) => navigate('/settings/email-templates/edit', { state: { id } })}
                    handleDelete={handleDelete}
                    handlePreview={handlePreview}
                    currentPageSize={currentPageSize}
                    handleChangePageSize={setCurrentPageSize}
                    handleChangePageNumber={setCurrentPageNumber}
                    currentPageNumber={currentPageNumber}
                    totalPageNumber={totalPageNumber}
                    hasNextPage={hasNextPage}
                />
            </div>
        </>
    );
};

export default EmailTemplates;